/** Matches Angular `formatShortcodeCode` — ensures `$variable` display. */
import {
    LR_ROW_ID_KEY,
    YARDI_ROW_ID_KEY,
} from '../../constants'

export { EVENT_REASONS_MAX_LENGTH, YARDI_ROW_ID_KEY, LR_ROW_ID_KEY } from '../../constants'

export function formatShortcodeCode(code: string | undefined | null): string {
    if (code == null || code === "") return "";
    const raw = String(code);
    return raw.startsWith("$") ? raw : `$${raw}`;
}

/** API may send 1, "1", or true for flags (Angular uses === 1 only; we accept common variants). */
export function isTruthyFlag(v: unknown): boolean {
    return v === 1 || v === true || v === "1";
}

/**
 * Backend often returns secrets as mask strings (e.g. `****`, `********abcd`, or `lib**********`).
 * Angular never patches those into `FormControl` values — it only binds them as `[placeholder]`, so
 * the input is empty and the first keystroke replaces the whole value. If we put the mask in React
 * state as `value`, typing appends after the asterisks.
 */
export function isYardiApiMaskedField(value: string | undefined | null): boolean {
    if (value == null || value === "") return false;
    const s = String(value).trim();
    if (s.length === 0) return false;
    if (/^\*+$/.test(s)) return true;
    if (/^[•・·]+$/.test(s)) return true;
    // Asterisks + short suffix hint, or short prefix + asterisks (e.g. lib**********, afq*************).
    if (/^\*{2,}[A-Za-z0-9]{1,16}$/.test(s)) return true;
    if (/^[A-Za-z0-9]{1,16}\*{2,}$/.test(s)) return true;
    return false;
}

/** Ensures optional event-row keys are JSON-serialized as `""` like Angular `getRawValue()`, not omitted/null. */
export function normalizeConfigEventRowStrings(
    row: Record<string, unknown>,
    keys: readonly string[],
): Record<string, unknown> {
    const out = { ...row };
    for (const k of keys) {
        if (out[k] === undefined || out[k] === null) out[k] = "";
    }
    return out;
}

/** `parameters` may be a JSON string or an already-parsed array from some clients. */
export function parseLrEventParameters(raw: unknown): Array<{ label: string; code: string }> | null {
    if (raw == null || raw === "") return null;
    if (Array.isArray(raw)) return raw as Array<{ label: string; code: string }>;
    if (typeof raw === "string") {
        try {
            const p = JSON.parse(raw);
            return Array.isArray(p) ? p : null;
        } catch {
            return null;
        }
    }
    return null;
}

/** Mirrors Angular `companyEditConfigForm` validators on `data_fetch_range_by_days`. */
export function getDataFetchRangeByDaysError(value: string | number | undefined | null): string | null {
    const v = String(value ?? "").trim();
    if (v === "") return "Data Fetch Range By Days is required.";
    const n = Number(v);
    if (Number.isNaN(n)) return "Data Fetch Range By Days is required.";
    if (n < 1) return "Data Fetch Range By Days must be at least 1.";
    if (n > 90) return "Data Fetch Range By Days cannot exceed 90.";
    return null;
}

export function newYardiRowId(): string {
    return crypto.randomUUID();
}

export function withYardiRowId(row: Record<string, unknown>): Record<string, unknown> {
    if (row[YARDI_ROW_ID_KEY]) return row;
    return { ...row, [YARDI_ROW_ID_KEY]: newYardiRowId() };
}

export function stripYardiRowIds(rows: Record<string, unknown>[]): Record<string, unknown>[] {
    return rows.map((r) => {
        const copy = { ...r };
        delete copy[YARDI_ROW_ID_KEY];
        return copy;
    });
}

export function newLrRowId(): string {
    return crypto.randomUUID();
}

export function withLrRowId(row: Record<string, unknown>): Record<string, unknown> {
    if (row[LR_ROW_ID_KEY]) return row;
    return { ...row, [LR_ROW_ID_KEY]: newLrRowId() };
}

export function stripLrRowIds(rows: Record<string, unknown>[]): Record<string, unknown>[] {
    return rows.map((r) => {
        const copy = { ...r };
        delete copy[LR_ROW_ID_KEY];
        return copy;
    });
}

/**
 * Same semantics as `@angular/cdk/drag-drop` `moveItemInArray` (also matches
 * `FormArray.removeAt` + `insertAt` indices from `CdkDragDrop`).
 */
export function moveItemInArray<T>(array: T[], fromIndex: number, toIndex: number): T[] {
    const copy = [...array];
    const n = copy.length;
    if (n === 0) return copy;
    const from = Math.max(0, Math.min(fromIndex, n - 1));
    const to = Math.max(0, Math.min(toIndex, n - 1));
    if (from === to) return copy;
    const target = copy[from]!;
    const delta = to < from ? -1 : 1;
    for (let i = from; i !== to; i += delta) {
        copy[i] = copy[i + delta]!;
    }
    copy[to] = target;
    return copy;
}

/**
 * Same as Angular `uniqueEventTypeActionCombinationValidatorForArray` in
 * `customvalidation.ts`: pairs `(event_type, lr_action)` with both values set
 * must be unique across rows.
 */
export function getDuplicateEventActionRowIndexes(
    rows: Array<{ event_type?: string; lr_action?: string }>,
): Set<number> {
    const combinationsMap = new Map<string, number[]>();
    rows.forEach((row, index) => {
        const eventType = String(row.event_type ?? "").trim();
        const lrAction = String(row.lr_action ?? "").trim();
        if (eventType && lrAction) {
            const key = `${eventType}::${lrAction}`;
            if (!combinationsMap.has(key)) {
                combinationsMap.set(key, []);
            }
            combinationsMap.get(key)!.push(index);
        }
    });
    const dup = new Set<number>();
    combinationsMap.forEach((indexes) => {
        if (indexes.length > 1) {
            indexes.forEach((i) => dup.add(i));
        }
    });
    return dup;
}

export function hasDuplicateEventActionCombinations(
    rows: Array<{ event_type?: string; lr_action?: string }>,
): boolean {
    return getDuplicateEventActionRowIndexes(rows).size > 0;
}

export const LR_SHORTCODE_POPUP_WIDTH = 380
/** Initial guess only — portal remeasures and re-anchors after paint. */
export const LR_SHORTCODE_POPUP_EST_HEIGHT = 140

/** Viewport `fixed` coords for the shortcodes portal, anchored to the info icon rect. */
export function computeLrShortcodePopupPosition(
    triggerRect: Pick<DOMRect, 'top' | 'left' | 'right' | 'bottom'>,
    viewport: Pick<Window, 'innerWidth' | 'innerHeight'> = window,
    panelHeight: number = LR_SHORTCODE_POPUP_EST_HEIGHT,
): { top: number; left: number; placement: 'right' | 'left' } {
    const margin = 12
    const gap = 8
    const vw = viewport.innerWidth
    const vh = viewport.innerHeight
    const height = Math.max(1, panelHeight)
    let left = triggerRect.right + 8
    let placement: 'right' | 'left' = 'right'
    if (left + LR_SHORTCODE_POPUP_WIDTH + margin > vw) {
        left = triggerRect.left - LR_SHORTCODE_POPUP_WIDTH - 8
        placement = 'left'
        if (left < margin) left = margin
    }

    // Prefer just below the icon so the panel clears the comment field.
    const preferredBelow = triggerRect.top + 28
    // Bottom of panel sits just above the icon when flipping up.
    const preferredAbove = triggerRect.top - height - gap
    const spaceBelow = vh - margin - preferredBelow
    const spaceAbove = triggerRect.top - margin

    let top: number
    if (spaceBelow >= height) {
        top = preferredBelow
    } else if (spaceAbove >= height) {
        top = preferredAbove
    } else if (spaceBelow >= spaceAbove) {
        top = Math.max(margin, Math.min(preferredBelow, vh - height - margin))
    } else {
        top = Math.max(margin, preferredAbove)
    }

    return { top, left, placement }
}

/** Read the open shortcodes panel height when present (for scroll/resize re-anchor). */
export function measureLrShortcodePopupHeight(): number {
    if (typeof document === 'undefined') return LR_SHORTCODE_POPUP_EST_HEIGHT
    const panel = document.querySelector<HTMLElement>('[data-lr-shortcode-popup]')
    const height = panel?.getBoundingClientRect().height
    return height && height > 0 ? height : LR_SHORTCODE_POPUP_EST_HEIGHT
}

export function lrShortcodeTriggerTestId(rowIndex: number): string {
    return `company-config-lr-event-row-${rowIndex}-shortcode-info-button`
}
